const STORAGE_KEY = 'stylon:shipTo:location';
const LEGACY_CITY_KEY = 'stylon:shipTo:city';
const OPTION_KEY_STORAGE_KEY = 'stylon:shipTo:optionKey';

export function normalizeDeliveryLocation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const address = String(raw.address || '').trim();
  const city = String(raw.city || '').trim();
  const country = String(raw.country || '').trim();
  const buildingNumber = String(raw.buildingNumber || '').trim();
  const apartment = String(raw.apartment || '').trim();
  const lat = raw.lat != null && Number.isFinite(Number(raw.lat)) ? Number(raw.lat) : null;
  const lng = raw.lng != null && Number.isFinite(Number(raw.lng)) ? Number(raw.lng) : null;
  // Which saved address (if any) this location came from — kept through the
  // localStorage round-trip so a page refresh can restore *which row* is
  // "active" (Checkout's `selectedAddressId`), not just the address text.
  // Previously this was set in memory (`applySavedAddress`) but dropped here,
  // so a refresh re-defaulted the active row to the MRU saved address even
  // when a different one was actually loaded.
  const savedAddressId = raw.savedAddressId != null ? String(raw.savedAddressId) : null;
  // Typed by hand in the manual form (street, city…) rather than located by
  // the address search — kept through a refresh so the form it was typed into
  // comes back, and so the checkout knows this address has no location ON
  // PURPOSE (see `deliveryAddressProblem`).
  const manual = raw.manual === true;
  if (!address && !city && lat == null) return null;
  return { address, city, country, buildingNumber, apartment, lat, lng, savedAddressId, manual };
}

/**
 * Was this address typed by hand? The manual form marks it; a SAVED address
 * without coordinates can only have been saved from that form (the search
 * always locates), so it counts too.
 */
export function isTypedByHand(location) {
  if (!location) return false;
  if (location.manual === true) return true;
  const unlocated = location.lat == null || location.lng == null;
  return unlocated && location.savedAddressId != null && location.savedAddressId !== '';
}

// The server's shortest delivery line (orders.service MIN_ADDRESS_LINE).
const MIN_ADDRESS_LINE = 4;

/**
 * WHAT STOPS THIS ADDRESS FROM BEING DELIVERED TO — null when nothing does.
 *
 * A located address (search result, current location) carries coordinates and
 * is priced by them. An address typed by hand has none, and the server is the
 * authority on what that means: its quote says `distancePriced: false` when
 * this shop's delivery price does not depend on where the address is (free or
 * the same price everywhere) — then it quotes by the city and places the order;
 * otherwise it refuses an order without coordinates (ADDRESS_PIN_REQUIRED). It
 * also refuses a delivery with no street or with neither a city nor a location
 * (ADDRESS_INCOMPLETE). A quote that does not say is read as needing the map.
 *
 *   'pin'      — nothing picked from the search: empty, or typed and never located
 *   'street'   — the manual form has no (usable) street
 *   'city'     — the manual form has no city
 *   'distance' — typed by hand, but this shop prices delivery by distance
 */
export function deliveryAddressProblem(location, quote) {
  const manual = isTypedByHand(location);
  const loc = normalizeDeliveryLocation(location);
  if (!loc?.address) return manual ? 'street' : 'pin';
  if (loc.lat != null && loc.lng != null) return null;
  if (!manual) return 'pin';
  if (quote?.distancePriced !== false) return 'distance';
  if (formatDeliveryAddressLine(loc).length < MIN_ADDRESS_LINE) return 'street';
  if (!loc.city) return 'city';
  return null;
}

/** The shopper-facing sentence for a `deliveryAddressProblem` reason. */
export function deliveryAddressProblemMessage(problem, t, quote) {
  switch (problem) {
    case 'pin':
      return quote?.distancePriced === false
        ? t('checkout.validation.locationOrTypeIt', {
          defaultValue: 'Pick your address from the search results, or type it in by hand.',
        })
        : t('checkout.validation.locationRequired', {
          defaultValue: 'Pick your address from search or use current location so we can calculate shipping.',
        });
    case 'street':
      return t('checkout.validation.streetRequired', { defaultValue: 'Enter the street and house number.' });
    case 'city':
      return t('checkout.validation.cityRequired', { defaultValue: 'Enter the city or town.' });
    case 'distance':
      return t('checkout.validation.addressNeedsMap', {
        defaultValue: "This shop’s delivery price depends on distance, so the address has to be found in the address search. Search for it again, or choose another option.",
      });
    default:
      return '';
  }
}

/**
 * Full address line for order submission (street + unit details).
 *
 * A geocoded address already names its house number ("הרצל 10"), and the
 * building field is auto-filled from the same geocode — appending it again, and
 * the apartment bare, read "הרצל 10, חיפה, ישראל, 10, 3" (UX-FINAL-17). A
 * building number the address already contains is not repeated, and with
 * `labels` ({ building, apartment }, in the shopper's language) what is added
 * is labelled: "…, דירה 3".
 */
export function formatDeliveryAddressLine(location, labels = null) {
  const loc = normalizeDeliveryLocation(location);
  if (!loc) return '';
  const parts = [loc.address];
  const words = new Set(String(loc.address || '').split(/[\s,]+/).filter(Boolean));
  const labelled = (label, value) => (label ? `${label} ${value}` : value);
  if (loc.buildingNumber && !words.has(loc.buildingNumber)) {
    parts.push(labelled(labels?.building, loc.buildingNumber));
  }
  if (loc.apartment) parts.push(labelled(labels?.apartment, loc.apartment));
  return parts.filter(Boolean).join(', ');
}

export function loadDeliveryLocation() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = normalizeDeliveryLocation(JSON.parse(raw));
      if (parsed) return parsed;
    }
    const legacyCity = localStorage.getItem(LEGACY_CITY_KEY);
    if (legacyCity) {
      return normalizeDeliveryLocation({ address: '', city: legacyCity, country: '', lat: null, lng: null });
    }
  } catch (_) { /* ignore */ }
  return null;
}

export function saveDeliveryLocation(location) {
  if (typeof localStorage === 'undefined') return;
  const normalized = normalizeDeliveryLocation(location);
  if (!normalized) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    if (normalized.city) localStorage.setItem(LEGACY_CITY_KEY, normalized.city);
  } catch (_) { /* ignore */ }
}

/**
 * Clear the persisted "ship to" location outright. `saveDeliveryLocation`
 * can't be used for this — passing a blank location normalizes to `null`
 * and is a no-op, silently leaving the previous (e.g. just-deleted) address
 * in storage. Used when the customer deletes their last saved address, so
 * the inline form doesn't reappear pre-filled with a now-gone address.
 */
export function clearDeliveryLocation() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_CITY_KEY);
  } catch (_) { /* ignore */ }
}

/** Short label for "Ship to …" summaries. */
export function deliveryLocationLabel(location) {
  if (!location) return '';
  if (location.city) return location.city;
  if (location.address) {
    const first = location.address.split(',')[0]?.trim();
    return first || location.address;
  }
  return '';
}

/**
 * The customer's chosen delivery/pickup option (e.g. "PICKUP" or
 * "DELIVERY:<tierId>") — persisted so a page refresh doesn't silently
 * re-default to pickup and strand an already-entered delivery address.
 * Checkout still re-validates the restored key against the freshly-resolved
 * shipping options before trusting it (see the `stillValid` check in
 * Pages/Checkout/index.js), so a stale/no-longer-offered tier safely falls
 * back to the normal default instead of being applied blindly.
 */
export function loadSelectedShippingOptionKey() {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(OPTION_KEY_STORAGE_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function saveSelectedShippingOptionKey(key) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (key) localStorage.setItem(OPTION_KEY_STORAGE_KEY, key);
    else localStorage.removeItem(OPTION_KEY_STORAGE_KEY);
  } catch (_) { /* ignore */ }
}

/** Body fields for POST /shipping-options. */
export function buildShippingLocationPayload(location) {
  const loc = normalizeDeliveryLocation(location);
  if (!loc) return {};
  return {
    addressLine: loc.address || undefined,
    city: loc.city || undefined,
    latitude: loc.lat ?? undefined,
    longitude: loc.lng ?? undefined,
  };
}
